import { CAZA, type SendaFight } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { lifeInD } from '../battle/reference.ts';
import { BRINK_RADIUS, MOOR_RADIUS, PENS_RADIUS, pensLayout, trailLayout } from '../battle/caza-layout.ts';
import { pensRules, trailRules } from '../battle/caza.ts';
import { createClearingMap } from '../map.ts';
import type { PensRules, Pt, TrailRules } from '../caza-types.ts';
import type { MapDef, SendaRules } from '../types.ts';
import { ARCHES_RADIUS, archesLayout, DEN_RADIUS, denLayout, LEAPS_RADIUS, leapsLayout } from '../battle/vertebras-layout.ts';
import { archesRules, denRules, leapsRules } from '../battle/vertebras.ts';
import type { VertRules } from '../vertebras-types.ts';
import { BOTE, VERT } from '@somnhold/content';
import { BOTE_RADIUS, elderLayout, swellLayout, wakeLayout } from '../battle/bote-layout.ts';
import { BOTE_ROWERS, elderRules, swellRules, wakeRules } from '../battle/bote.ts';
import type { BoteRules } from '../bote-types.ts';
import { ISLA } from '@somnhold/content';
import { FLOES_RADIUS, floesLayout, LAGOON_RADIUS, lagoonLayout, SEAL_RADIUS, sealLayout } from '../battle/isla-layout.ts';
import { FLOES_CYCLE, floesRules, lagoonRules, sealRules } from '../battle/isla.ts';
import type { IslaRules } from '../isla-types.ts';
import { ROCA } from '@somnhold/content';
import { BED_RADIUS, bedLayout, CLIMB_RADIUS, climbLayout, HALL_RADIUS, hallLayout, WOLF_RADIUS, wolfLayout } from '../battle/roca-layout.ts';
import { bedRules, climbRules, mouthsRules, wolfRules } from '../battle/roca.ts';
import type { RocaRules } from '../roca-types.ts';
import type { SendaState } from './types.ts';
import { idiv, imax, imin } from '../math/fixed.ts';

/**
 * The Long Hunt's grounds (the barbarian's path, chapter 1; docs/sendas/barbaro.md): the broken corral (`pens`, act 1),
 * the pastures' tracks in the rain (`tracks`, act 2) and the ledge over the sea (`brink`, act 3, the test). Apart from
 * campaign/sendas.ts (docs/SENDAS.md, «Para no chocar»): it asks this file for the ground and its people.
 */

/** The Long Hunt's grounds. */
export const cazaGround = (act: SendaFight): boolean => act.mode === 'pens' || act.mode === 'tracks' || act.mode === 'brink' || act.mode === 'arches' || act.mode === 'leaps' || act.mode === 'den' || act.mode === 'wake' || act.mode === 'swell' || act.mode === 'elder' || act.mode === 'floes' || act.mode === 'lagoon' || act.mode === 'seal' || act.mode === 'climb' || act.mode === 'mouths' || act.mode === 'ashbed' || act.mode === 'wolf';

/** Their own life and blow over the chapter's commons. */
export const cazaOwn = (act: SendaFight): { hpBp: number; dmgBp: number } | null =>
  act.mode === 'pens' ? CAZA.pens : act.mode === 'tracks' ? CAZA.trail.tracks : act.mode === 'brink' ? CAZA.trail.brink : act.mode === 'arches' ? VERT.arches : act.mode === 'leaps' ? VERT.leaps : act.mode === 'den' ? VERT.den : act.mode === 'wake' ? BOTE.wake : act.mode === 'swell' ? BOTE.swell : act.mode === 'elder' ? BOTE.elder : act.mode === 'floes' ? ISLA.floes : act.mode === 'lagoon' ? ISLA.lagoon : act.mode === 'seal' ? ISLA.seal : act.mode === 'climb' ? ROCA.climb : act.mode === 'mouths' ? ROCA.mouths : act.mode === 'ashbed' ? ROCA.bed : act.mode === 'wolf' ? ROCA.wolf : null;

export interface CazaGround {
  params: BattleParams;
  pens?: PensRules;
  trail?: TrailRules;
  /** Chapter 2's (the arches, the leaps, the den). */
  vert?: VertRules;
  start: Pt;
  /** Where Sten stands (the den). */
  sten?: Pt;
  /** Chapter 3's (the wake, the swell, the old howler), and where each of the boat's people sits. */
  bote?: BoteRules;
  seats?: Record<string, Pt>;
  /** Chapter 4's (the floes, the lagoon, Sigrun's last night). */
  isla?: IslaRules;
  /** Chapter 5's (the climb, the mouths, the ash bed, the Grey Wolf). */
  roca?: RocaRules;
  /** Where Bera and Terrón stand as it starts. */
  bera: Pt;
  terron: Pt;
}

/** A path's own ground in place of the saga's: no roads, no node to keep, no golem; its scaling kept. */
const ownParams = (base: BattleParams, map: MapDef): BattleParams => {
  const { objectiveTicks: _o, objectiveQuietRate: _q, ...rest } = base;
  void _o;
  void _q;
  return { ...rest, map, spawns: [], keepHp: 1_000_000, keepMaxHp: 1_000_000, rules: { ...(base.rules?.enemyHpBp ? { enemyHpBp: base.rules.enemyHpBp } : {}) } };
};

/** The ground of a Long Hunt's act from the seed, its lives in seconds of D of the reference hero (`ref`). */
export const ownCaza = (act: SendaFight, base: BattleParams, seed: number, ref: { d: number }, st?: SendaState): CazaGround => {
  // Chapter 5, «La guarida»: the black rock in the sea.
  if (act.mode === 'climb' || act.mode === 'mouths' || act.mode === 'ashbed' || act.mode === 'wolf') return ownRoca(act, base, seed, ref, st);
  if (act.mode === 'pens') {
    const L = pensLayout(seed);
    const map: MapDef = { ...createClearingMap(PENS_RADIUS), rocks: L.rocks };
    const at = { x: L.gateAt.x, y: L.gateAt.y - 500 };
    return { params: ownParams(base, map), pens: pensRules(L, lifeInD(ref, CAZA.pens.wolfD)), start: L.start, bera: { x: at.x - 450, y: at.y }, terron: { x: at.x + 450, y: at.y + 100 } };
  }
  // Chapter 2, «Las Vértebras»: the sea everywhere but the arches.
  if (act.mode === 'arches') {
    const L = archesLayout(seed);
    const map: MapDef = { ...createClearingMap(ARCHES_RADIUS), rocks: L.rocks };
    const p0 = L.path[0] as Pt;
    return { params: ownParams(base, map), vert: { kind: 'arches', arches: archesRules(L, lifeInD(ref, VERT.arches.wolfD)) }, start: L.start, bera: { x: p0.x - 300, y: p0.y + 600 }, terron: { x: p0.x + 300, y: p0.y + 500 } };
  }
  if (act.mode === 'leaps') {
    const L = leapsLayout(seed);
    const map: MapDef = { ...createClearingMap(LEAPS_RADIUS), rocks: L.rocks };
    return { params: ownParams(base, map), vert: { kind: 'leaps', leaps: leapsRules(L) }, start: L.start, bera: L.start, terron: L.start };
  }
  if (act.mode === 'den') {
    const L = denLayout(seed);
    const map: MapDef = { ...createClearingMap(DEN_RADIUS), rocks: L.rocks };
    const den = denRules(L, lifeInD(ref, VERT.den.wolfD), lifeInD(ref, VERT.den.howlerD), lifeInD(ref, CAZA.stone.d));
    return { params: ownParams(base, map), vert: { kind: 'den', den }, start: L.start, bera: L.bera, terron: L.terron, sten: L.sten };
  }
  // Chapter 3, «El bote»: Bera's long boat in the middle of the sea.
  if (act.mode === 'wake' || act.mode === 'swell' || act.mode === 'elder') {
    const L = act.mode === 'wake' ? wakeLayout(seed) : act.mode === 'swell' ? swellLayout(seed) : elderLayout(seed);
    const map: MapDef = { ...createClearingMap(BOTE_RADIUS), rocks: L.rocks };
    const wolf = lifeInD(ref, BOTE.wake.wolfD);
    const bote: BoteRules =
      act.mode === 'wake'
        ? { kind: 'wake', wake: wakeRules(L as ReturnType<typeof wakeLayout>, wolf) }
        : act.mode === 'swell'
          ? { kind: 'swell', swell: swellRules(L as ReturnType<typeof swellLayout>, wolf) }
          : { kind: 'elder', elder: elderRules(L as ReturnType<typeof elderLayout>, wolf, lifeInD(ref, BOTE.elder.d)) };
    // Each rower at its oar, a little inboard; Bera at the tiller; the boat itself (a thing to keep) amidships.
    const seats: Record<string, Pt> = { bera: { ...L.boat.tiller }, boat: { ...L.boat.mid } };
    L.boat.oars.forEach((p, k) => {
      seats[BOTE_ROWERS[k] as string] = { x: p.x - (p.x > 0 ? 250 : -250), y: p.y };
    });
    return { params: ownParams(base, map), bote, start: L.start, bera: L.boat.tiller, terron: seats.terron as Pt, seats };
  }
  // Chapter 4, «La Isla Blanca»: the island's shore and floes, the lagoon, the island by night.
  if (act.mode === 'floes') {
    const L = floesLayout(seed, FLOES_CYCLE);
    const map: MapDef = { ...createClearingMap(FLOES_RADIUS), rocks: L.rocks };
    const isla: IslaRules = { kind: 'floes', floes: floesRules(L, lifeInD(ref, ISLA.floes.wolfD), lifeInD(ref, ISLA.floes.colossusD)) };
    return { params: ownParams(base, map), isla, start: L.start, bera: L.seats.bera as Pt, terron: L.seats.terron as Pt, sten: L.seats.sten as Pt, seats: L.seats };
  }
  if (act.mode === 'lagoon') {
    const L = lagoonLayout(seed);
    const map: MapDef = { ...createClearingMap(LAGOON_RADIUS), rocks: L.rocks };
    const isla: IslaRules = { kind: 'lagoon', lagoon: lagoonRules(L, lifeInD(ref, ISLA.lagoon.wolfD)) };
    return { params: ownParams(base, map), isla, start: L.start, bera: { x: L.start.x - 900, y: L.start.y + 500 }, terron: { x: L.start.x + 900, y: L.start.y + 400 } };
  }
  if (act.mode === 'seal') {
    const L = sealLayout(seed);
    const map: MapDef = { ...createClearingMap(SEAL_RADIUS), rocks: L.rocks };
    const isla: IslaRules = { kind: 'seal', seal: sealRules(L, lifeInD(ref, ISLA.seal.wolfD), lifeInD(ref, ISLA.seal.d), lifeInD(ref, ISLA.seal.howler.d)) };
    return { params: ownParams(base, map), isla, start: L.start, bera: L.start, terron: L.start, seats: { fire: { ...L.fire } } };
  }
  const kind = act.mode === 'brink' ? 'brink' : 'tracks';
  const L = trailLayout(seed, kind);
  const map: MapDef = { ...createClearingMap(kind === 'brink' ? BRINK_RADIUS : MOOR_RADIUS), rocks: L.rocks };
  const trail = trailRules(L, lifeInD(ref, CAZA.trail.wolfD), lifeInD(ref, CAZA.howler.d), lifeInD(ref, CAZA.stone.d));
  return { params: ownParams(base, map), trail, start: L.start, bera: { x: L.start.x - 900, y: L.start.y + 700 }, terron: { x: L.start.x + 600, y: L.start.y + 400 } };
};

/** Bera and Terrón where the act puts them. */
export const placeCazaPeople = (g: CazaGround, sr: SendaRules): void => {
  for (const n of sr.npcs) {
    if (n.id === 'bera') Object.assign(n, { x: g.bera.x, y: g.bera.y });
    if (n.id === 'terron') Object.assign(n, { x: g.terron.x, y: g.terron.y });
    if (n.id === 'sten' && g.sten) Object.assign(n, { x: g.sten.x, y: g.sten.y });
    const seat = g.seats?.[n.id];
    if (seat) Object.assign(n, { x: seat.x, y: seat.y });
  }
};

/** A blow of `cd` hundredths of a second of D (Ulla's, Ivar's). */
const hitOf = (ref: { d: number }, cd: number): number => imax(1, idiv(ref.d * cd, 100));

/**
 * The grounds of chapter 5 (docs/sendas/barbaro.md, «La guarida»): the rock's wall, its cave's hall, the way down to the
 * ash bed and the Wolf's den and summit. The fight reads what the path kept: which mouths act 2 shut and whether the
 * one without tracks stands (`nums.shut`, `nums.bare`; who never played it, the puzzle's best), Sigrun's axe taken or
 * left, and the Wolf's scars.
 */
const ownRoca = (act: SendaFight, base: BattleParams, seed: number, ref: { d: number }, st?: SendaState): CazaGround => {
  const ulla = hitOf(ref, ROCA.ulla.hitCd);
  if (act.mode === 'climb') {
    const L = climbLayout(seed);
    const map: MapDef = { ...createClearingMap(CLIMB_RADIUS), rocks: L.rocks };
    const roca: RocaRules = { kind: 'climb', climb: climbRules(L, lifeInD(ref, ROCA.climb.wolfD), lifeInD(ref, ROCA.climb.crowD), lifeInD(ref, ROCA.climb.howler.d), ulla) };
    const near = { x: L.start.x - 600, y: L.start.y + 300 };
    return { params: ownParams(base, map), roca, start: L.start, bera: near, terron: { x: L.start.x - 1200, y: L.start.y }, seats: { ulla: near } };
  }
  if (act.mode === 'mouths') {
    const L = hallLayout(seed);
    const map: MapDef = { ...createClearingMap(HALL_RADIUS), rocks: L.rocks };
    const roca: RocaRules = { kind: 'mouths', mouths: mouthsRules(L, lifeInD(ref, ROCA.mouths.wolfD), lifeInD(ref, ROCA.mouths.cubD), ulla) };
    const near = { x: L.start.x - 600, y: L.start.y + 300 };
    return { params: ownParams(base, map), roca, start: L.start, bera: near, terron: { x: L.start.x + 700, y: L.start.y + 200 }, seats: { ulla: near, ivar: L.ivar, hunter: L.hunter } };
  }
  if (act.mode === 'ashbed') {
    const L = bedLayout(seed);
    const map: MapDef = { ...createClearingMap(BED_RADIUS), rocks: L.rocks };
    const roca: RocaRules = { kind: 'bed', bed: bedRules(L, lifeInD(ref, ROCA.bed.wolfD), lifeInD(ref, ROCA.bed.cubD), ulla) };
    const near = (dx: number, dy: number): { x: number; y: number } => ({ x: L.start.x + dx, y: L.start.y + dy });
    return { params: ownParams(base, map), roca, start: L.start, bera: near(-600, 200), terron: near(600, 300), seats: { ulla: near(-600, 200), ivar: near(500, 700), sten: near(-300, 900) } };
  }
  const L = wolfLayout(seed);
  const map: MapDef = { ...createClearingMap(WOLF_RADIUS), rocks: L.rocks };
  const flags = st?.flags ?? [];
  const nums = st?.nums ?? {};
  // (Who never played act 2, the lab: two mouths shut, the one without tracks standing, as its best leaves it.)
  const shut = nums.shut ?? 0b1001;
  const bare = nums.bare ?? 2;
  const roca: RocaRules = {
    kind: 'wolf',
    wolf: wolfRules(L, {
      hp: lifeInD(ref, ROCA.wolf.d),
      wolfHp: lifeInD(ref, ROCA.wolf.wolfD),
      cubHp: lifeInD(ref, ROCA.wolf.cubD),
      ullaHit: ulla,
      ivarHit: hitOf(ref, ROCA.ivar.hitCd),
      shut,
      bare,
      axe: !flags.includes('axeLeft'),
      scars: imin(2, st?.scars ?? 0),
    }),
  };
  return { params: ownParams(base, map), roca, start: L.start, bera: L.ulla, terron: { x: L.start.x + 700, y: L.start.y + 300 }, seats: { ulla: L.ulla, ivar: L.ivarHurt, sten: L.sten, skiff: L.skiff } };
};
